import { Component } from '@theme/component';

class BrunoPurchaseSelector extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    this.dialog = this.querySelector('dialog');
    this.brand = this.querySelector('[data-purchase-brand]');
    this.model = this.querySelector('[data-purchase-model]');
    this.status = this.querySelector('[data-purchase-status]');
    this.next = this.querySelector('[data-purchase-next]');
    this.submit = this.querySelector('[data-purchase-submit]');
    this.retry = this.querySelector('[data-purchase-retry]');
    this.step = 1;
    const options = { signal: this.controller.signal };
    const allCatalog = this.querySelector('[data-purchase-all-catalog]');
    if (allCatalog) {
      allCatalog.href = this.preservePreview(new URL(allCatalog.href, window.location.origin)).href;
      allCatalog.addEventListener('click', (event) => {
        if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && this.dialog.open) this.dialog.close();
      }, options);
    }
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest('[data-purchase-open], a[href="#bruno-purchase"]');
      if (!trigger) return;
      event.preventDefault();
      this.open();
    }, options);
    this.querySelector('[data-purchase-close]').addEventListener('click', () => this.dialog.close(), options);
    this.dialog.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); }, options);
    this.dialog.addEventListener('close', () => {
      if (document.querySelector('[data-bruno-moto] dialog[open], bruno-purchase-selector dialog[open]')) return;
      document.body.style.overflow = this.previousOverflow || '';
      if (this.previousFocus instanceof HTMLElement && this.previousFocus.isConnected) this.previousFocus.focus({ preventScroll: true });
    }, options);
    document.addEventListener('bruno:moto-selector:opening', (event) => {
      if (event.detail?.dialog !== this.dialog && this.dialog.open) {
        this.dialog.close();
        document.body.style.overflow = this.previousOverflow || '';
      }
    }, options);
    this.brand.addEventListener('change', () => {
      this.brand.setCustomValidity('');
      this.populateModels();
      this.next.disabled = !this.selectedBrand();
    }, options);
    this.model.addEventListener('change', () => {
      this.model.setCustomValidity('');
      this.submit.disabled = !this.selectedModel();
    }, options);
    this.next.addEventListener('click', () => this.goToModel(), options);
    this.querySelector('[data-purchase-back]').addEventListener('click', () => this.showStep(1), options);
    this.retry.addEventListener('click', () => this.loadCatalog(), options);
    this.querySelector('form').addEventListener('submit', (event) => {
      event.preventDefault();
      if (this.step === 1) { this.goToModel(); return; }
      const model = this.selectedModel();
      this.model.setCustomValidity(model ? '' : this.dataset.modelError);
      if (!model || !this.model.reportValidity()) return;
      const destination = new URL(this.dataset.searchUrl, window.location.origin);
      destination.searchParams.set('q', `tag:"${model.searchTag}"`);
      destination.searchParams.set('type', 'product');
      destination.searchParams.set('options[prefix]', 'none');
      this.preservePreview(destination);
      this.dialog.close();
      window.location.assign(destination.href);
    }, options);
    this.resetSelection();
    window.addEventListener('pageshow', () => this.resetSelection(), options);
    if (window.location.hash === '#bruno-purchase') this.open();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.loadController?.abort();
    if (this.dialog?.open) {
      this.dialog.close();
      document.body.style.overflow = this.previousOverflow || '';
    }
    super.disconnectedCallback();
  }

  selectedBrand() { return this.catalog?.find((item) => item.name === this.brand.value); }
  selectedModel() { return this.selectedBrand()?.models.find((item) => item.tag === this.model.value); }

  populateModels() {
    this.model.replaceChildren(new Option(this.dataset.modelPlaceholder, ''));
    const brand = this.selectedBrand();
    for (const model of brand?.models || []) this.model.add(new Option(model.name, model.tag));
    this.model.disabled = !brand;
    this.submit.disabled = true;
    this.querySelector('[data-purchase-chosen]').textContent = brand?.name || '';
  }

  showStep(step) {
    this.step = step;
    this.querySelectorAll('[data-purchase-step]').forEach((element) => { element.hidden = Number(element.dataset.purchaseStep) !== step; });
    this.querySelectorAll('[data-purchase-progress]').forEach((element) => {
      if (Number(element.dataset.purchaseProgress) === step) element.setAttribute('aria-current', 'step');
      else element.removeAttribute('aria-current');
    });
    if (this.dialog.open) (step === 1 ? this.brand : this.model).focus({ preventScroll: true });
  }

  goToModel() {
    this.brand.setCustomValidity(this.selectedBrand() ? '' : this.dataset.brandError);
    if (this.selectedBrand() && this.brand.reportValidity()) this.showStep(2);
  }

  async loadCatalog() {
    if (this.catalog?.length) return true;
    if (this.loading) return this.loading;
    this.status.textContent = this.dataset.loading;
    this.retry.hidden = true;
    this.loadController = new AbortController();
    this.loading = (async () => {
      try {
        const response = await fetch(this.dataset.catalogUrl, { credentials: 'omit', signal: this.loadController.signal });
        if (!response.ok) throw new Error('Catalog unavailable');
        const data = await response.json();
        if (!Array.isArray(data.brands)) throw new Error('Invalid catalog');
        this.catalog = data.brands.filter((brand) => typeof brand.name === 'string' && Array.isArray(brand.models)).map((brand) => ({
          name: brand.name,
          models: brand.models.filter((model) => typeof model.name === 'string' && /^bruno-moto-[a-z0-9-]+$/.test(model.tag) && typeof model.searchTag === 'string' && model.searchTag.length && !/["\\]/.test(model.searchTag))
        })).filter((brand) => brand.models.length);
        if (!this.catalog.length) throw new Error('Empty catalog');
        this.brand.replaceChildren(new Option(this.dataset.brandPlaceholder, ''));
        for (const brand of this.catalog) this.brand.add(new Option(brand.name, brand.name));
        this.brand.disabled = false;
        this.status.textContent = '';
        this.populateModels();
        return true;
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.status.textContent = this.dataset.loadError;
          this.retry.hidden = false;
        }
        return false;
      } finally { this.loading = null; }
    })();
    return this.loading;
  }

  resetSelection() {
    this.brand.value = '';
    this.brand.setCustomValidity('');
    this.model.setCustomValidity('');
    this.populateModels();
    this.next.disabled = true;
    this.showStep(1);
  }

  preservePreview(url) {
    const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }

  async open() {
    if (this.dialog.open) return;
    this.resetSelection();
    document.dispatchEvent(new CustomEvent('bruno:moto-selector:opening', { detail: { dialog: this.dialog } }));
    this.previousFocus = document.activeElement;
    this.previousOverflow = document.body.style.overflow;
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    this.showStep(1);
    if (await this.loadCatalog()) {
      if (this.dialog.open) this.brand.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('bruno-purchase-selector')) customElements.define('bruno-purchase-selector', BrunoPurchaseSelector);


